import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { DealSearchQueryInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-search-query.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import { DealService } from '@zaoblako/web/core/services/deal/deal.service';
import { StatusLabelComponent } from '@zaoblako/web/shared/components/status-label/status-label.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';

@Component({
  selector: 'crm-deal-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent, StatusLabelComponent, SpinnerComponent],
  templateUrl: './deal-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealMultiSelectComponent implements OnInit {
  @ViewChild('multiSelectComponent', { static: true }) public multiSelectComponent: MultiSelectComponent;

  @Input() public deals: DealFindInterface[] = [];
  public queryParams: DealSearchQueryInterface = {
    limit: 50,
    offset: 0
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedDeals: DealFindInterface[] = [];
  @Input() public set selectedDeals(selectedDeals: DealFindInterface[]) {
    if (!this.customSearch) {
      if (!selectedDeals?.every((selectedDeal) => this.deals.some((deal) => deal.id === selectedDeal.id))) {
        const ids = selectedDeals
          ?.filter((selectedDeal) => !this.deals.some((deal) => deal.id === selectedDeal.id))
          .map((selectedDeal) => selectedDeal.id);
        if (ids?.length) {
          ids.forEach((id, index) => {
            if (typeof id === 'string') {
              ids[index] = +id;
            }
          });
        }
        if (ids?.length) {
          this.dealService.flatList({ ids: ids, limit: ids?.length > 500 ? 500 : ids?.length, offset: 0 }).subscribe({
            next: (data) => {
              this.deals = this.deals.concat(
                data.items.filter((item) => !this.deals.some((deal) => deal.id === item.id))
              );
              this.cdRef.markForCheck();
              setTimeout(() => {
                this._selectedDeals = this.deals.filter((deal) => ids.includes(deal.id));
                this.cdRef.markForCheck();
              }, 30);
            }
          });
        }
      }
    } else {
      this._selectedDeals = selectedDeals;
    }
  }

  @Output() public selectedDealsChange = new EventEmitter<DealFindInterface[]>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = false;
  @Input() public placeholder = '\u00A0';
  @Input() public isShowStatus = true;

  @Input() public customSearch = false;

  @Input() public set setSelectedDealsIds(ids: number[]) {
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }
    if (
      ids?.length &&
      ((this._selectedDeals?.length && !this._selectedDeals?.every((selectedDeal) => ids.includes(selectedDeal.id))) ||
        !this._selectedDeals?.length)
    ) {
      let newIds = ids.filter((id) => !this.deals.some((deal) => deal.id === id));
      if (newIds?.length) {
        this.dealService
          .flatList({ ids: newIds, limit: newIds?.length > 500 ? 500 : newIds?.length, offset: 0 })
          .subscribe({
            next: (data) => {
              this.deals = this.deals.concat(
                data.items.filter((item) => !this.deals.some((deal) => deal.id === item.id))
              );
              this.cdRef.markForCheck();
              setTimeout(() => {
                this._selectedDeals = this.deals.filter((deal) => ids.includes(deal.id));
                this.cdRef.markForCheck();
              }, 30);
            }
          });
      } else {
        this._selectedDeals = this.deals.filter((deal) => ids.includes(deal.id));
      }
    }
  }

  public constructor(private readonly dealService: DealService, private readonly cdRef: ChangeDetectorRef) {}

  public ngOnInit() {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.dealService.search(this.queryParams).subscribe({
        next: (data) => {
          this.deals = this.deals?.length
            ? this.deals.concat(data.items.filter((item) => !this.deals.some((deal) => deal.id === item.id)))
            : data.items.filter((item) => !this.deals.some((deal) => deal.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public toDeal(deal: any): DealFindInterface {
    return deal as DealFindInterface;
  }

  public onSelect(deals: DealFindInterface[]) {
    this.selectedDealsChange.emit(deals);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.multiSelectComponent?.multiSelect?.scroller.onScroll.subscribe(() => {
          let el: HTMLElement = this.multiSelectComponent?.multiSelect?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.deals.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.dealService.search(this.queryParams).subscribe({
                next: (data) => {
                  this.deals = this.deals.concat(
                    data.items.filter((item) => !this.deals.some((deal) => deal.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public searchDeal(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.dealService.search(this.queryParams).subscribe({
        next: (data) => {
          this.deals = data.items.concat(
            this._selectedDeals?.filter((selectedDeal) => !data.items.some((deal) => deal.id === selectedDeal.id))
          );
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }
}
